/**
 * `@resource-kit/model` — the schema algebra, extracted from the host (#2057).
 *
 * WHAT LIVES HERE: everything needed to DECLARE a resource — the field types,
 * the field/model/view definitions, the two `define*View` helpers, the
 * query-builder's column vocabulary (its closed operator set, column schema and
 * condition shape, #2166), and the resource-level contracts that name no
 * rendering type: actions, quick filters, presets, pagination, permissions.
 *
 * WHAT DOES NOT MOVE HERE: the legacy `EntityField` / `ActionDefinition` /
 * `ResourceDetailConfig` family. It still lives in the host — a
 * `render?: ComponentType` or a `headerActions: ReactNode` says what a
 * declaration MEANS on screen, which is the render axis this package sits
 * below (ADR-0035 D4; those move to the controllers entry in #2167, not here).
 * The one React-typed shape already in this file is `Widget` (its
 * `display` / `input` are the field-type registry's rendering contract), which
 * is why the boundary below tolerates a type-only `react` import.
 *
 * Compiled by `./tsconfig.json`, a standalone project whose whole job is to
 * PROVE, in the transitive-closure sense, that this package never reaches into
 * the host app: `paths: {}` makes any `@/…` alias a TS2307, `rootDir: "."` makes
 * any relative escape a TS6059, and real emit is what makes tsc check `rootDir`
 * at all (ADR-0032 §5). It may name exactly three things outside itself:
 * `@resource-kit/core`, and type-only `react` / `zod`.
 *
 * THIS FILE IS ALSO AN AUGMENTATION TARGET, and carries the same trap as
 * `@resource-kit/core`'s root entry: `declare module` merges into the module
 * that DECLARES an interface, so `AuditResourceTypeRegistry` below is declared
 * here and must never be re-exported from a host shell — a second export path
 * invites augmenting the wrong module, which merges into nothing and silently
 * collapses the registry to `never`.
 */
import type { ComponentType } from 'react';
import type { ZodType } from 'zod';
import type { ModelName, WidgetName, WidgetRegistry } from '@resource-kit/core';
import type { Modifier, ModifierCtx } from '@resource-kit/core';

/**
 * Every field type a resource may declare — the COMPLETENESS AUTHORITY.
 *
 * Written out as a literal union rather than derived from the host's
 * `FIELD_TYPE_REGISTRY` (which is where it used to be derived from), because
 * the derivation pointed the wrong way: the host registry is one CONSUMER of
 * this set — it maps each type to five host-shaped projections — and a package
 * cannot take its own vocabulary from a consumer.
 *
 * The inversion does not weaken the completeness check, it relocates it. The
 * registry's `satisfies Record<FieldType, FieldTypeSpec>`
 * (`packages/resource-kit-ui/react/registry/field-type-registry.tsx::FIELD_TYPE_REGISTRY`)
 * now checks the REGISTRY against this union: a type added here with no registry
 * row fails there, and a registry row for a type absent here fails as an excess
 * property. `createFieldConstructors` below closes the same loop inside the
 * package with its two `satisfies Record<CoreFieldType, …>` maps — which is why
 * no runtime tuple of these names exists anywhere: nothing needs a value-level
 * list, and a second copy would be a drift source with no way to detect drift.
 */
export type FieldType =
  | 'text'
  | 'number'
  | 'currency'
  | 'date'
  | 'datetime'
  | 'boolean'
  | 'email'
  | 'phone'
  | 'enum'
  | 'image'
  | 'json'
  | 'array'
  | 'id';

/** Historical alias, kept because ~a dozen host modules name this spelling. */
export type CoreFieldType = FieldType;

/**
 * Wire value sent when the operator CLEARS a field (#1797).
 *
 * Deliberately not a global constant: this repo's catalog string PATCH accepts
 * `''` and rejects `null`, while the delivery operator-note route is
 * `.nullable()` and must receive `null` to clear. The default comes from the
 * field type (`@resource-kit/controllers`' `form/field-value-policy.ts`); a field sets
 * this when its endpoint disagrees.
 */
export type EmptyEncoding = '' | null;

/** One choice of an `enum` field. */
export interface EnumOption {
  value: string | number;
  label: string;
  color?: string; // For badges (e.g., 'success', 'warning')
}

/**
 * The translator a non-component projection is handed.
 *
 * Injected rather than reached for through the i18n singleton: a CSV export
 * must be in the operator's ACTIVE language, and the projection must stay
 * unit-testable. CSV projections only ever need a default-value fallback, so
 * this narrow signature keeps the injected translator trivially stubbable.
 */
export type TranslateFn = (key: string, opts?: { defaultValue?: string }) => string;

/**
 * The filter vocabulary a field's values are queried through — the host's
 * QueryBuilder column type. It lives here because `Widget.filterType` is part
 * of the widget contract. The host used to re-export it from
 * `components/query/types.ts` so existing importers kept their spelling; #2170
 * deleted that shim, and they name `@resource-kit/model` directly.
 */
export type DataType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'enum'
  | 'date'
  | 'datetime'
  | 'id'
  | 'json';

// Closed list of every URL `<op>:<value>` shorthand the QueryBuilder can
// produce. The backend parses that shorthand in
// `backend/src/platform/http/list-query.ts::parseListQuery` and translates it
// to SQL in `backend/src/platform/db/list-filter.ts::buildListWhere`, both
// against its own
// hand-written `FILTER_OPS` tuple — the two workspaces cannot import each
// other, so nothing shares a literal here.
//
// What keeps them from drifting is
// `backend/src/__tests__/node/operator-parity-1743.test.ts`: it reads BOTH
// source files, extracts the two tuples textually, and fails if they are
// not the same closed set (and fails loud if either declaration changes
// shape). It runs in backend `npm test`, i.e. on every PR. Adding or
// removing an op means changing both tuples and that test's expected
// count, in one PR — which is the point.
//
// `as const` narrows to a literal-tuple type `OperatorId` derives from.
export const OPERATOR_IDS = [
  'eq',
  'ne',
  'lt',
  'gt',
  'like',
  'ilike',
  'in',
  'from',
  'to',
  'between',
] as const;

export type OperatorId = (typeof OPERATOR_IDS)[number];

export type ValueKind =
  | 'none'
  | 'text'
  | 'number'
  | 'enum_single'
  | 'enum_multi'
  | 'date'
  | 'dateRange'
  | 'numberRange';

export type ColumnSchema = {
  id: string;
  label: string;
  type: DataType;
  enumValues?: { value: string; label?: string }[];
  // The column's backend `filterable` whitelist (per-field closed op set).
  // When set, the QueryBuilder trims the op dropdown to it; when absent the
  // full registry for `type` is offered (#contains-filter).
  operations?: readonly OperatorId[];
};

export type Condition = {
  id: string;
  field: string;
  op: OperatorId;
  value?: unknown;
};

/**
 * The OPEN audit-resource-type registry. Empty by construction — this package
 * never learns which of a project's resources the backend's data-change ledger
 * covers, but `ModelDef` must still refuse a value outside that closed set.
 *
 * The host adds the one row, in the module that owns the generated enum:
 *
 * ```ts
 * declare module '@resource-kit/model' {
 *   interface AuditResourceTypeRegistry { type: ResourceAuditResourceType }
 * }
 * ```
 *
 * Same idiom, same augmentation target and the same trap as `WidgetRegistry` /
 * `ModelRegistry` in `@resource-kit/core` — see this file's header.
 *
 * Note the shape difference from those two: they are keyed BY name (their
 * membership is the key set), while this registry carries a single `type`
 * member whose VALUE is the closed union. The union is generated from the API
 * contract as one alias; splitting it into keys here would be a second,
 * hand-maintained copy of exactly the enum the generator exists to own.
 */
export interface AuditResourceTypeRegistry {}

/**
 * The audit resource types the host has declared — `never` until something
 * augments the registry above, which is what makes an un-augmented project
 * unable to set `auditResourceType` at all rather than able to set anything.
 */
export type RegisteredAuditResourceType = AuditResourceTypeRegistry extends { type: infer T }
  ? T
  : never;

export const CORE_VIEW_TYPE = {
  list: 'list',
  form: 'form',
  search: 'search',
} as const;

export type CoreViewType = (typeof CORE_VIEW_TYPE)[keyof typeof CORE_VIEW_TYPE];
export type ViewType = CoreViewType;

export interface ModelField {
  type: FieldType;
  label: string;
  required?: boolean;
  options?: readonly EnumOption[];
  /**
   * many2one: the model this field's value is an id INTO. Closed to the
   * declared model names, so a misspelled target fails `tsc` (design §3).
   * The renderer is `@resource-kit/ui/react`'s `relation` surface.
   */
  relation?: ModelName;
  /**
   * one2many / many2many: the field on the OTHER model that points back here.
   * Deliberately still a bare `string`: it names a column, not a model, and
   * closing it needs the target's field set — which nothing reads yet, since
   * the inverse side has no renderer.
   */
  inverse?: string;
  currencyField?: string;
  widget?: WidgetName;
  schema?: ZodType<unknown>;
  /**
   * Wire value for a CLEARED field, overriding the field type's repo default
   * (#1797). It belongs to the model, not a view: which of `''` / `null` an
   * endpoint accepts is a property of the record's contract, and every view
   * that clears the field must send the same one.
   */
  encodeEmpty?: EmptyEncoding;
  /**
   * `false` ⇒ this field generates no filter at all (#1805).
   *
   * Same argument as `encodeEmpty` directly above: it belongs to the model,
   * not a view. A computed or display-only column has NO backend key behind
   * it, so there is no view in which it could be filtered — the absence is a
   * property of the field itself. Absent ⇒ filterable.
   */
  filterable?: boolean;
  /**
   * The BACKEND key this field filters on, when it differs from the field's
   * own name (#1805).
   *
   * Also a model property, for the same reason: which key a field filters on
   * is a property of the record's contract, and every view that filters the
   * field must send the same one. `auditResource` is the live case — the
   * display columns `actor_label` / `route_path` filter the backend's
   * `actor` / `action`, and `deriveQueryColumnsFromFields` intersects THESE
   * ids with the backend whitelist, so a missing one silently removes the
   * filter rather than mis-naming it.
   */
  filterId?: string;
  /**
   * `'utc'` ⇒ a bare `YYYY-MM-DD` filter bound on this INSTANT column names a
   * UTC day rather than a merchant business day (#2105). Absent ⇒ merchant.
   *
   * A model property for the same reason `filterId` above is one: which
   * calendar a filter bound is resolved against is a property of what the
   * column RECORDS, so every view that filters it must agree. Business-record
   * instants (an order placed, a menu plan approved) answer in the operator's
   * business day, because that is the day they mean. Foreign-system and
   * forensic instants — a provider's webhook emission, an audit row — keep
   * UTC, because their whole value is lining up against somebody else's log,
   * and re-anchoring them to a merchant day breaks exactly that.
   *
   * Deliberately a one-member union rather than a boolean or a full
   * `'merchant' | 'utc'`: the default is not a policy that could flip, it is
   * what an operator means, so only the exception is worth declaring — and
   * `instantReading: 'merchant'` would be a declaration that says nothing.
   *
   * Only read for a field whose filter type resolves to an instant; on a
   * `date` column it is meaningless (a day column is a merchant calendar day
   * by construction) and on a non-temporal column it is ignored.
   */
  instantReading?: 'utc';
  /**
   * `true` ⇒ the value is the server's to assign and NO ONE may write it,
   * on any surface, in any mode (#1871).
   *
   * Not the same concept as `readonly`, and the two must not be merged again:
   *
   *   - `readonly` (`ViewItem` / `EntityField`) is DOMAIN STATE — "this
   *     shipment is delivered, freeze the date". It depends on the record, so
   *     it may be a predicate, it is meaningless on a create form (there is
   *     no record whose state could forbid anything), and a view may
   *     legitimately override it, because different screens may judge state
   *     differently.
   *   - `serverAssigned` is the RECORD'S CONTRACT — the primary key, the
   *     audit quartet, a foreign system's key. It depends on nothing, so it
   *     holds on the create form too, and no view may override it: `ViewItem`
   *     simply has no such member, which is the whole implementation of
   *     "not overridable" — a view that tries fails `tsc`.
   *
   * Deliberately a plain `boolean`, not a `Modifier`: a predicate needs a
   * `ModifierCtx` and the model has no record in hand. Were it allowed to
   * vary with state it would just be `readonly` again.
   */
  serverAssigned?: boolean;
}

/**
 * The `virtual` discriminant, written out rather than borrowed.
 *
 * It used to be `Pick<…, 'virtual'>` off the host's legacy `EntityField<T>`
 * union, which is exactly the kind of host dependency this package exists to
 * shed — and the borrowing bought nothing, because the two members it produces
 * are one word each.
 *
 * `T` IS PHANTOM ON `EntityFieldDef`, AND DELIBERATELY KEPT. It constrains
 * nothing here (the `key`-against-`T` check lives in `CheckedFields`, which is
 * where a field learns the name it is bound to). Dropping it would be a
 * source-breaking change at ~100 existing `EntityFieldDef<unknown>` sites for
 * no gain, and it keeps the entity/virtual pair reading as one parameterised
 * family at every call site.
 *
 * It was phantom BEFORE this move too, which is why the `Pick` survives the
 * rewrite: the old form was `Pick<LegacyEntityBranch<T>, 'virtual'>`, and that
 * `Pick` was already discarding everything `T` touched. Only the source of the
 * discriminant changed — from the host's legacy union to the two words below.
 * The `Pick` is load-bearing all the same: `noUnusedParameters` is on in this
 * package's gate, so a `T` that appears nowhere is TS6133, and the honest
 * choices are to consume it here or to delete it and break the call sites.
 */
export type EntityFieldDef<T> = ModelField & Pick<{ virtual?: false; phantom?: T }, 'virtual'>;

export type VirtualFieldDef = ModelField & { virtual: true };

export type AnyFieldDef = EntityFieldDef<unknown> | VirtualFieldDef;

type FieldConfig<T, K extends CoreFieldType> = Omit<
  ModelField,
  'type' | 'currencyField' | 'options'
> &
  // `currencyField` is OPTIONAL on a currency field, not mandatory (#1805
  // slice S6). Requiring it made `f.currency` unwritable: the legacy bridge
  // fails hard on any field that carries one (`UNRENDERABLE_FIELD_PROPS`),
  // so a mandatory member meant every currency column was rejected. Both
  // currency columns in this repo already rely on the renderer's `'USD'`
  // default (`packages/resource-kit-ui/react/registry/field-type-registry.tsx::CurrencyDisplay`
  // and the registry's own
  // `packages/resource-kit-ui/react/registry/field-type-registry.tsx::FIELD_TYPE_REGISTRY.currency`
  // CSV projection).
  // The ban stays as it is: a DECLARED `currencyField` still fails hard,
  // because dropping it
  // quietly would let an author believe a column drives the symbol when it
  // does not.
  (K extends 'currency'
    ? { currencyField?: Extract<keyof T, string> }
    : { currencyField?: never }) &
  (K extends 'enum' ? { options?: readonly EnumOption[] } : { options?: never });

type EntityFieldConstructor<T, K extends CoreFieldType> = (
  config: FieldConfig<T, K>,
) => EntityFieldDef<T> & { type: K };

type VirtualFieldConstructor<T, K extends CoreFieldType> = (
  config: FieldConfig<T, K>,
) => VirtualFieldDef & { type: K };

export type FieldConstructors<T> = {
  [K in CoreFieldType]: EntityFieldConstructor<T, K>;
} & {
  virtual: {
    [K in CoreFieldType]: VirtualFieldConstructor<T, K>;
  };
};

type AnyEntityFieldConstructor<T> = {
  [K in CoreFieldType]: EntityFieldConstructor<T, K>;
}[CoreFieldType];

type AnyVirtualFieldConstructor<T> = {
  [K in CoreFieldType]: VirtualFieldConstructor<T, K>;
}[CoreFieldType];

/**
 * Contract properties a field type carries unless the author overrides them.
 *
 * `id` is the only one: an id is an IDENTITY, not editable data. A primary key
 * is the server's to mint, and a foreign system's key (`shopify_id`,
 * `onfleet_id`, `trace_id`) is that system's — offering either in a form
 * invites a write the backend will reject or, worse, honour. Operators still
 * READ them (comparing against the Shopify admin is daily work), which is why
 * this is `serverAssigned` and not `invisible`.
 *
 * A `relation` id is excluded, and the exclusion is PART OF THE RULE rather
 * than an exception to it: a many2one's value is a reference the operator
 * picks, so nothing about it is server-assigned. Deciding that here, where the
 * constructor already holds the config, is what keeps the whole rule in one
 * place — the alternative (freeze every id, let each many2one opt back out)
 * leaves half the rule at twenty call sites, waiting to be forgotten.
 */
function typeDefaults(
  type: CoreFieldType,
  config: { relation?: ModelName },
): { serverAssigned?: boolean } {
  return type === 'id' && config.relation === undefined ? { serverAssigned: true } : {};
}

function entityFieldConstructor<T, K extends CoreFieldType>(type: K): EntityFieldConstructor<T, K> {
  return (config) => ({ ...typeDefaults(type, config), ...config, type });
}

function virtualFieldConstructor<T, K extends CoreFieldType>(
  type: K,
): VirtualFieldConstructor<T, K> {
  return (config) => ({ ...typeDefaults(type, config), ...config, type, virtual: true });
}

/**
 * Bind concrete field constructors to a wire type. Both constructor maps are
 * closed over `FieldType`; adding a member to that union without adding both
 * constructors fails this declaration, which is what makes the union's
 * completeness self-enforcing inside the package.
 */
export function createFieldConstructors<T>(): FieldConstructors<T> {
  const entity = {
    text: entityFieldConstructor<T, 'text'>('text'),
    number: entityFieldConstructor<T, 'number'>('number'),
    currency: entityFieldConstructor<T, 'currency'>('currency'),
    date: entityFieldConstructor<T, 'date'>('date'),
    datetime: entityFieldConstructor<T, 'datetime'>('datetime'),
    boolean: entityFieldConstructor<T, 'boolean'>('boolean'),
    email: entityFieldConstructor<T, 'email'>('email'),
    phone: entityFieldConstructor<T, 'phone'>('phone'),
    enum: entityFieldConstructor<T, 'enum'>('enum'),
    image: entityFieldConstructor<T, 'image'>('image'),
    json: entityFieldConstructor<T, 'json'>('json'),
    array: entityFieldConstructor<T, 'array'>('array'),
    id: entityFieldConstructor<T, 'id'>('id'),
  } satisfies Record<CoreFieldType, AnyEntityFieldConstructor<T>>;

  const virtual = {
    text: virtualFieldConstructor<T, 'text'>('text'),
    number: virtualFieldConstructor<T, 'number'>('number'),
    currency: virtualFieldConstructor<T, 'currency'>('currency'),
    date: virtualFieldConstructor<T, 'date'>('date'),
    datetime: virtualFieldConstructor<T, 'datetime'>('datetime'),
    boolean: virtualFieldConstructor<T, 'boolean'>('boolean'),
    email: virtualFieldConstructor<T, 'email'>('email'),
    phone: virtualFieldConstructor<T, 'phone'>('phone'),
    enum: virtualFieldConstructor<T, 'enum'>('enum'),
    image: virtualFieldConstructor<T, 'image'>('image'),
    json: virtualFieldConstructor<T, 'json'>('json'),
    array: virtualFieldConstructor<T, 'array'>('array'),
    id: virtualFieldConstructor<T, 'id'>('id'),
  } satisfies Record<CoreFieldType, AnyVirtualFieldConstructor<T>>;

  return { ...entity, virtual };
}

/**
 * Which read-only surface a `display` projection is rendering on (#1805).
 *
 * Orthogonal to `mode`, which chooses display vs input: this chooses WHICH
 * display. An id truncates in a table row and never in a detail row; a json
 * value counts its keys in a table row and tabulates them in a detail row.
 * Without this axis those two types cannot be expressed as one widget, and the
 * type defaults extracted in #1805 would have had to guess. Absent ⇒ `'list'`
 * for direct widget consumers; column builders always pass the surface.
 */
export type DisplaySurface = 'list' | 'detail';

/**
 * What the field slot owns and an `input` projection must forward to its DOM
 * node (#1797, carried to widgets in #1805).
 *
 * None of it is derivable from `field`: `readOnly` is "a save is in flight or
 * this operator may not edit", `invalid` / `describedBy` are the live
 * validation issue, and `id` is the historical `form-<key>` the `<Label
 * htmlFor>`, the DatePicker test id and the e2e specs all address. A widget
 * that dropped them would render a control that looks right and is unlabelled.
 */
export interface WidgetControl {
  /** DOM id of the control — what `<Label htmlFor>` points at. */
  id: string;
  /** The field's label, already translated by the slot. */
  label: string;
  readOnly?: boolean;
  invalid?: boolean;
  describedBy?: string;
  onBlur?: () => void;
}

export interface WidgetProps<V = unknown> {
  value: V;
  record: unknown;
  field: ModelField;
  mode: 'display' | 'input';
  onChange?: (next: V) => void;
  /** `mode: 'display'` only — see `DisplaySurface`. */
  surface?: DisplaySurface;
  /** `mode: 'input'` only — see `WidgetControl`. */
  control?: WidgetControl;
}

/**
 * What a widget's CSV projection is given (#1805 slice S5).
 *
 * The three members beyond `value` are not decoration — each is the ONLY way to
 * reproduce something the legacy carrier this projection replaces could already
 * do, and a narrower context would have made manifest §2's mapping a promise
 * the contract cannot keep:
 *
 *   `record`    — `fields[].transform` is `(value, row) => derived`, and the
 *                 repo's live case (`auditResource.actor_label`) reads ONLY the
 *                 row. Without it that column exports nothing.
 *   `field`     — the type projections are functions OF the field: `currency`
 *                 reads `currencyCode`, `enum` reads the option table.
 *   `translate` — injected rather than reached for through the i18n singleton,
 *                 for the same reason `formatFieldTextForExport` injects it:
 *                 the export must be in the operator's ACTIVE language, and the
 *                 projection must stay unit-testable.
 *
 * It mirrors `WidgetProps` (`value` / `record` / `field`) on purpose: a widget
 * author writing `display` and `csvText` reads the same three names for the
 * same three things. `translate` is the one addition — `display` is a component
 * and can call `useTranslation`; `csvText` is a plain function and cannot.
 */
export interface WidgetCsvContext<V = unknown> {
  value: V;
  record: unknown;
  field: ModelField;
  translate: TranslateFn;
}

export interface Widget<V = unknown> {
  display: ComponentType<WidgetProps<V>>;
  /**
   * The form control. OPTIONAL since ADR-0030: a read-only widget has none of
   * its own, and resolution refills it from the field type's default like the
   * other fallback projections — so omitting it means "the control this field
   * type would have shown", not "no control".
   */
  input?: ComponentType<WidgetProps<V> & Required<Pick<WidgetProps<V>, 'onChange'>>>;
  csvText?: (ctx: WidgetCsvContext<V>) => string;
  filterType?: DataType;
  zodBase?: ZodType<unknown>;
  supportedTypes: readonly FieldType[];
  modes: readonly ('list' | 'form')[];
}

/**
 * A named widget's declaration, narrowed to the types and modes its registry
 * row declares. Runtime registration lives in
 * `@resource-kit/controllers`' `widgets/registry` — one catalog, one resolution
 * order, one failure policy (#1799). It sat in the host's
 * `src/resource-kit/ui/widget-registry` until #2167 PR-3 moved it; the
 * re-export shim it left at that path is gone since #2170.
 *
 * This INTERSECTS `Widget` rather than `Omit`-ing its two metadata fields
 * (design §5.2 wrote the `Omit` form). Both narrow a concrete callsite
 * identically, but the intersection also makes `WidgetFor<N>` provably a
 * `Widget` for an UNRESOLVED `N` — which is what lets `registerWidget<N>` store
 * the widget without a cast. It additionally makes a row that declares a
 * non-`FieldType` (`types: 'bogus'`) unregisterable instead of merely useless.
 *
 * Known upper bound (see `WidgetRegistry` in `@resource-kit/core`): this blocks
 * a superset, not a subset.
 */
export type WidgetFor<N extends WidgetName> = Widget & {
  supportedTypes: readonly WidgetRegistry[N]['types'][];
  modes: readonly WidgetRegistry[N]['modes'][];
};

export type FieldPathSegment = string;

export type FieldPath<T = unknown> = readonly [
  T extends object ? Extract<keyof T, string> : string,
  ...FieldPathSegment[],
];

export interface Issue<T = unknown> {
  path: FieldPath<T>;
  code: string;
  message: string;
}

export type RecordValidator<T> = (
  record: T,
  ctx: {
    mode: 'create' | 'update';
    changed: ReadonlySet<keyof T>;
  },
) => Issue<T>[] | Promise<Issue<T>[]>;

export interface RecordNode<T> {
  id: string | null;
  tempId?: string;
  initial: T | null;
  draft: Partial<T>;
  touched: ReadonlySet<keyof T>;
  changed: ReadonlySet<keyof T>;
  version?: string;
  issues: Issue<T>[];
}

export const STALE_RECORD = 'STALE_RECORD' as const;

export interface ConflictPolicy<T> {
  onStale(ctx: {
    node: RecordNode<T>;
    current: T;
    version: string;
    retryCount: number;
  }): Promise<{ kind: 'retry'; node: RecordNode<T> } | { kind: 'abort'; issues: Issue<T>[] }>;
}

/**
 * The pagination / sort / search envelope a list view is driven by.
 *
 * Defined here and consumed by the host's `useDataTableParams`, which declares
 * `DataTableParams = ListParams` — the reverse of the old direction, where this
 * package's name was an alias of a host hook's interface. The shape is a
 * property of the LIST CONTRACT, not of the React hook that happens to
 * serialise it into the URL.
 */
export interface ListParams {
  page: number;
  limit: number;
  sort: string;
  search?: string;
  extraParams?: Record<string, string>;
}

export interface ModelDef<T, F extends Record<string, AnyFieldDef> = Record<string, AnyFieldDef>> {
  name: string;
  idField: Extract<keyof T, string>;
  recName: Extract<keyof T, string>;
  fields: F;
  validate?: RecordValidator<T>;
  /**
   * Which row of the backend's data-change ledger this resource writes to.
   * Omit when the ledger does not cover the resource — that omission is also
   * what keeps the change-history tab off its form
   * (`packages/resource-kit-ui/react/form/FormView.tsx::FormView`).
   *
   * Typed as the CLOSED enum the host augments in through
   * `AuditResourceTypeRegistry`, not a free `string`: the value is sent as
   * `resource_type` on `/api/resource-audit`, so a typo would compile and
   * then query a type the backend never returns — an always-empty history tab
   * that reads as "this record was never touched". The union is derived from
   * the API contract and augmented in, never re-listed here; a second
   * value-level copy of it would be a drift source with no way to detect the
   * drift.
   */
  auditResourceType?: RegisteredAuditResourceType;
  /**
   * Which field carries the id the LEDGER keys this resource's rows on
   * (#1963). Defaults to `idField`.
   *
   * These are two different key spaces and only look like one. `idField` is
   * how the resource is ADDRESSED — it lands in the detail URL and in
   * `api.update`, and for products / customers that is the Shopify id. The
   * ledger writers store the row's internal UUID primary key instead
   * (`contexts/commerce/store/*.ts` — `before?.id ?? newId()`). Where the two
   * coincide (`orders`, `dispatch/jobs`, whose `idField` IS `id`) the default
   * is right and the declaration is noise; where they diverge, omitting it
   * queries a key the ledger has never written, and the tab renders EMPTY
   * rather than erroring — a record that looks like it was never touched.
   *
   * Naming a field the record does not carry hides the tab (and fails loud in
   * dev) rather than falling back to `idField`: the fallback IS the bug this
   * exists to prevent, and a silently wrong key space is worse than no tab.
   */
  auditResourceIdField?: Extract<keyof T, string>;
}

export type CheckedFields<T, F extends Record<string, AnyFieldDef>> = {
  [K in keyof F]: F[K] extends { virtual: true }
    ? VirtualFieldDef
    : K extends keyof T
      ? EntityFieldDef<T>
      : never;
};

/**
 * Columns the backend assigns and no client may ever send.
 *
 * The audit quartet and the soft-delete tombstone (ADR-0011, written by
 * `platform/db/shared-fields.ts`): their value is a fact ABOUT the write, so a
 * form that offered them would either be ignored or corrupt the audit trail.
 * `tenant_id` is the same kind of fact — it comes from the authenticated
 * request, and a client that could choose it could write into another tenant.
 *
 * Matched by exact name on purpose. A suffix rule (`endsWith('_at')`) would
 * swallow the business timestamps an operator legitimately edits —
 * `completed_at`, `picked_at`, `delivery_date` — and a provider's own audit
 * columns (`onfleet_created_at`) are that provider's record, not ours; they are
 * already unwritable for the ordinary reason that no endpoint accepts them.
 *
 * Names with no carrier in this app YET (`created_by` / `updated_by` /
 * `deleted_at`) are listed anyway: the point of a default is that the field
 * added next year needs no one to remember this rule.
 */
const SERVER_ASSIGNED_FIELDS: ReadonlySet<string> = new Set([
  // Redundant with the `id` TYPE default today — every declared primary key
  // in this app is an `f.id()`, which already carries `serverAssigned: true`.
  // Kept because the two rules cover different holes: the type rule catches a
  // foreign system's key whatever it is named (`shopify_id`, `trace_id`),
  // this one catches a primary key declared as some other type.
  'id',
  'created_at',
  'created_by',
  'updated_at',
  'updated_by',
  'deleted_at',
  'tenant_id',
]);

/**
 * Apply the by-NAME `serverAssigned` default. It cannot live in the field
 * constructors (`f.datetime({...})` never sees the key it is bound to) and it
 * must not live in a view (every view would have to repeat it), so the model is
 * where the record's contract gets its last word. An explicit `serverAssigned`
 * on the field still wins — the default only fills an absent one.
 */
function applyServerAssignedDefaults<F extends Record<string, AnyFieldDef>>(fields: F): F {
  return Object.fromEntries(
    Object.entries(fields).map(([key, field]) => [
      key,
      SERVER_ASSIGNED_FIELDS.has(key) && field.serverAssigned === undefined
        ? { ...field, serverAssigned: true }
        : field,
    ]),
  ) as F;
}

export function defineModel<T>() {
  return <const N extends string, F extends Record<string, AnyFieldDef>>(
    cfg: Omit<ModelDef<T, F>, 'name' | 'fields'> & {
      name: N;
      fields: F & CheckedFields<T, F>;
    },
  ): ModelDef<T, F> & { name: N } => ({
    ...cfg,
    fields: applyServerAssignedDefaults(cfg.fields),
  });
}

export interface ViewItem<T, F extends Record<string, AnyFieldDef>> {
  field: Extract<keyof F, string>;
  widget?: WidgetName;
  label?: string;
  invisible?: Modifier<T>;
  readonly?: Modifier<T>;
  columnInvisible?: Modifier<T>;
  /**
   * Keep this field off the DETAIL surface entirely — the mirror of
   * `columnInvisible`, for the field that exists only to carry a filter id.
   * Unlike `columnInvisible` it is absolute: the detail surface has no column
   * picker to switch it back on.
   *
   * Declaring it is also what excuses the field from the "every rendered
   * column belongs to a `fields` group" contract `FormView` enforces, so the
   * two are one decision rather than two: a field is either grouped or
   * declared absent, never quietly swept into a leftovers bucket.
   */
  detailInvisible?: Modifier<T>;
  required?: Modifier<T>;
  inlineSave?: boolean;
}

export interface ListViewDef<T, F extends Record<string, AnyFieldDef>> {
  items: readonly ViewItem<T, F>[];
  searchFields?: readonly Extract<keyof F, string>[];
  capabilities?: Partial<
    Record<'search' | 'filter' | 'sort' | 'export' | 'print' | 'select', boolean>
  >;
  defaultSort?: {
    field: Extract<keyof F, string>;
    dir: 'asc' | 'desc';
  };
}

/**
 * How a group lays out its children (#2783). Every group declares it,
 * including the form root; there is no default, because a default of
 * `'tab'` would push a forgotten nested group into the rule below.
 *
 * - `'tab'`: one rail tab per child; the trigger carries the child's
 *   `label`. Only the form ROOT may use it: a page has one selected tab, one
 *   `PageShell` tab slot, and a failed save reveals one level, so a nested
 *   rail would escape all three. `FormView` fails loud on a nested `'tab'`.
 * - `'card'`: each child is drawn as a `FormGroup` — a card on a full page,
 *   a titled section in a drawer.
 * - `'inline'`: each child is drawn as a titled section in the same block,
 *   on a full page and in a drawer alike.
 *
 * A closed set: widening it would need an ADR.
 */
export type GroupDisplay = 'card' | 'tab' | 'inline';

/** What every node of the form tree carries, leaf or group (#2783). */
interface FormNodeBase {
  /** Unique across the whole tree: groups and leaves share one namespace. */
  id: string;
  /**
   * The child's title: the rail trigger under a `'tab'` parent (required
   * there), the card or section heading under `'card'` / `'inline'`
   * (optional there; an untitled child draws no heading).
   */
  label?: string;
  /**
   * Fold the node behind a click toggle (#1805 slice S6). Requires a
   * `label`, which is what the toggle shows.
   *
   * SCOPE: only when the node is drawn by `FormGroup`, i.e. its parent group
   * is `'card'` or `'inline'`. As a rail tab (#1918) both flags are ignored,
   * because the rail trigger already is the progressive-disclosure
   * mechanism and a panel folded shut under its own active trigger is a
   * click that reveals nothing. This is a decision, not an omission:
   * `FormView`'s fields branch carries the matching note, and
   * `FormView.groupDisplay.test.tsx` asserts the ignore.
   */
  collapsible?: boolean;
  /**
   * Only meaningful with `collapsible` under a `'card'` / `'inline'`
   * parent: start folded on first paint. Ignored on the rail — see above.
   */
  defaultCollapsed?: boolean;
}

/**
 * A content leaf of the form tree. `fields` reads the loaded record;
 * `custom` and `audit` fetch their own data when they mount, so `FormView`
 * mounts them only when they are first shown.
 */
export type FormLeaf<T, F extends Record<string, AnyFieldDef>> =
  | (FormNodeBase & { kind: 'fields'; items: readonly ViewItem<T, F>[] })
  | (FormNodeBase & {
      kind: 'custom';
      render: ComponentType<{ record: T }>;
      /**
       * Declares that a gate EXISTS on this node (#1917) — it is NOT the
       * verdict. The verdict arrives at runtime through
       * `FormViewProps.tabGates`, keyed by this node's `id`, so the identity
       * fact stays with the host and the kit never reads an auth hook.
       *
       * `true` is the only value: a `false` here would read as "no gate" and
       * silently fail OPEN, and a boolean slot invites `gated={isAdmin}` at
       * the declaration site, which is the identity fact in the wrong place.
       * Absent ⇒ visible; declared but unwired ⇒ hidden.
       */
      gated?: true;
    })
  | (FormNodeBase & { kind: 'audit' });

/**
 * The form root: a group without `kind`, `id` or `label` (#2783). Its
 * `groupDisplay` lays out its `children` exactly as a nested group's does.
 */
export interface FormViewDef<T, F extends Record<string, AnyFieldDef>> {
  groupDisplay: GroupDisplay;
  children: readonly FormNode<T, F>[];
}

/** A nested group: the root's two fields plus a node identity. */
export type FormGroupNode<T, F extends Record<string, AnyFieldDef>> = FormViewDef<T, F> &
  FormNodeBase & { kind: 'group' };

export type FormNode<T, F extends Record<string, AnyFieldDef>> =
  | FormLeaf<T, F>
  | FormGroupNode<T, F>;

export function defineListView<T, F extends Record<string, AnyFieldDef>>(
  _model: ModelDef<T, F>,
  def: ListViewDef<T, F>,
): ListViewDef<T, F> {
  return def;
}

export function defineFormView<T, F extends Record<string, AnyFieldDef>>(
  _model: ModelDef<T, F>,
  def: FormViewDef<T, F>,
): FormViewDef<T, F> {
  return def;
}

/** Every node under `group`, depth-first, in declared order. */
export function formNodesOf<T, F extends Record<string, AnyFieldDef>>(
  group: FormViewDef<T, F>,
): readonly FormNode<T, F>[] {
  return group.children.flatMap((child) =>
    child.kind === 'group' ? [child, ...formNodesOf(child)] : [child],
  );
}

// ─── Resource-level contracts (moved from the host's `types/schema.ts`, #2166)
// Every declaration below states WHAT a resource offers — a modifier, an
// action, a quick filter, a pagination mode, a permission map — without naming
// how any of it renders. That is what makes them declarations rather than
// projections, and therefore what puts them on this side of the render axis
// (ADR-0035 D4). Their host-side neighbours that DO carry a `ComponentType` or
// a `ReactNode` stayed behind; `@/types/schema` re-exports these so the ~70
// modules importing them from there are untouched.

/**
 * A field modifier in this (flat, pre-view) field definition (#1799).
 *
 * The record is nullable because none of the three surfaces here always has
 * one: a create form has no record yet, and a list column's default visibility
 * is a table-level decision with no single row behind it. A predicate must
 * therefore cope with its absence — which is also why it cannot be used to
 * express a per-row rule on a surface that has no row.
 */
export type FieldModifier<T> = Modifier<T | null>;

/** The context a `FieldModifier` predicate receives. */
export type FieldModifierCtx<T> = ModifierCtx<T | null>;

/**
 * Per-surface presentation opt-out. Absent ⇒ rendered.
 *
 * `list` is deliberately NOT a member: a list column's default visibility is
 * `columnInvisible`, which keeps the column filterable and exportable while
 * leaving it out of the default column set. Hiding a field from the detail or
 * form surface is genuinely "do not render this here".
 */
export interface FieldInvisibility<T> {
  detail?: FieldModifier<T>;
  form?: FieldModifier<T>;
}

export interface StaticOptions {
  source: 'static';
  items: EnumOption[];
}

/** Static-copy confirm variant: a fixed title + optional description. */
export interface ActionConfirm {
  title: string;
  description?: string;
  /**
   * i18n key for the dialog's confirm / dismiss button. Absent ⇒ the host
   * dialog's generic label. Set the dismiss label when the action's own label
   * is the generic one ("Cancel"), so one word never means both "do it" and
   * "back out". Honoured by the bulk-action runner only.
   */
  confirmText?: string;
  cancelText?: string;
}

/**
 * Result-branch feedback kinds (#1416, capability 2). `as const` map → derived
 * union so a feedback callback can never return a magic-string kind the runner
 * doesn't route (the runner dispatches through a total `Record` over these).
 */
export const ACTION_FEEDBACK_KIND = {
  success: 'success',
  info: 'info',
  error: 'error',
} as const;
export type ActionFeedbackKind = (typeof ACTION_FEEDBACK_KIND)[keyof typeof ACTION_FEEDBACK_KIND];

/** A result-branch feedback outcome: a closed-map kind + an i18n message key. */
export interface ActionFeedback {
  kind: ActionFeedbackKind;
  /** i18n key for the toast body. */
  message: string;
}

/**
 * Row-action dispatch mode (#1416, capability 4). `as const` map → union.
 *   - `execute` (default): the full confirm → execute → feedback → invalidate
 *     pipeline, with the row entering the shared busy registry while it runs.
 *   - `handoff`: hands control back to the page — NO confirm, NO toast, NO
 *     busy entry — invoking only `onAction` (a closure the page captured when
 *     it built the definition in-component; see the note on `rowActions`).
 */
export const ACTION_MODE = {
  execute: 'execute',
  handoff: 'handoff',
} as const;
export type ActionMode = (typeof ACTION_MODE)[keyof typeof ACTION_MODE];

/**
 * Post-success panel behavior for an action run from a detail-panel footer
 * (#1417). `as const` map → derived union so a definition can never declare a
 * magic-string mode the footer runner doesn't route.
 *   - `refetch` (default): keep the panel open — the runner already
 *     invalidated the resource cache, so the panel re-reads fresh data.
 *   - `close`: additionally dismiss the panel (close the drawer / navigate
 *     back) — e.g. a resolve that moves the record out of the open inbox.
 */
export const AFTER_ACTION = {
  close: 'close',
  refetch: 'refetch',
} as const;
export type AfterAction = (typeof AFTER_ACTION)[keyof typeof AFTER_ACTION];

export interface ColumnFilterMeta {
  type?: DataType;
  label: string;
  fieldId: string;
  enumValues?: EnumOption[];
}

export interface ResourceFormConfig {
  createTitle?: string;
  editTitle?: string;
  createButtonLabel?: string;
  columns?: 1 | 2;
  maxWidth?: 'sm' | 'md' | 'lg';
}

export interface BulkActionDefinition<T> {
  id: string;
  label: string;
  permission?: string;
  confirm?: ActionConfirm;
  intent?: 'default' | 'danger';
  isEligible?: (row: T) => boolean;
  disabledReason?: string;
  onAction: (rows: T[]) => Promise<unknown>;
  formatResult?: (result: unknown) => string;
}

/** Detail-surface modes for `ResourceDetailConfig.surface` (#1392 D6). */
export type DetailSurface = 'drawer' | 'page' | 'both';

/**
 * A quick filter surfaced in the toolbar (#1392 D4). Each quick filter writes
 * to the SAME `conditions` state the QueryBuilder uses — a `select` writes an
 * `eq` condition on `field`, a `daterange` writes a `between` condition — so
 * there is exactly one filter/URL source of truth. Every `field` MUST appear
 * in the resource's `filterable` whitelist (render-time assertion — throws in
 * dev/test, logs in a production build; see `assertQuickFiltersWhitelisted`).
 */
export type QuickFilter =
  | {
      field: string;
      type: 'select';
      /** Option `label` is an i18n key resolved with `t()` at render. */
      options: readonly { value: string; label: string }[];
    }
  | { field: string; type: 'daterange' };

/**
 * A named filter preset surfaced above the table as a one-click
 * toggle. Each preset names a stable id (for selection state +
 * test selectors), an i18n label key, and the conditions that
 * apply when the preset is active. Clicking an active preset
 * clears the conditions (resets to `defaultConditions`); clicking
 * a different preset swaps the conditions atomically.
 *
 * Conditions inside a preset are built by a function, not stored as a
 * static array, because some presets depend on the calendar day (see
 * jobResource `preset.today` for the canonical example). The list
 * controller hands that day in as `today`: the calendar day
 * (`YYYY-MM-DD`) in the operating time zone (`format.timeZone`), read
 * from the kit's `useToday()`. The controller calls `buildConditions`
 * again when that day changes (#2780), so a page left open over
 * midnight moves to the new day. A preset must NOT read the clock
 * itself: the day it uses must be the one the controller hands in.
 */
export interface FilterPreset {
  /** Stable id — used as React key + as the active-preset marker.
   *  Convention: `<resource>.preset.<name>` mirrors the i18n key
   *  but is consumed by the runtime, not i18next. */
  id: string;
  /** i18n key for the button label (e.g. `job.preset.today`). */
  label: string;
  /** Builds the preset's conditions for `today` (`YYYY-MM-DD`, operating
   *  time zone), which the list controller passes in on hydration, on a
   *  click, and again when the day changes. The parameter is declared
   *  REQUIRED so every caller must hand a day in, and the type documents
   *  that a preset gets its day from the controller. It does NOT stop a
   *  preset from ignoring it: TypeScript accepts a zero-argument function
   *  here. Using `today` instead of the clock is a convention that review
   *  enforces (#2780). */
  buildConditions: (today: string) => Condition[];
}

export const RESOURCE_PAGINATION = {
  server: 'server',
  client: 'client',
} as const;

export type ResourcePagination = (typeof RESOURCE_PAGINATION)[keyof typeof RESOURCE_PAGINATION];

export type ResourcePermission = {
  list?: string;
  read?: string;
  create?: string;
  update?: string;
  delete?: string;
};
